import { useTranslation } from "react-i18next"

import Chip from "@/components/chip/Chip"
import { tokenizeFormula, getOperatorDisplay } from "../utils/formulaTokenizer"
import { MetricMeta } from "@/types/metric"
import { useTranslateMetric } from "@/hooks/metric/useTranslateKeyHook"

interface Props {
  formula: string
  availableMetrics?: MetricMeta[]
  testValues?: Record<string, number>
  showResult?: boolean
  result?: number | null
}

export default function FormulaDisplay({
  formula,
  availableMetrics = [],
  testValues,
  showResult = false,
  result,
}: Props) {
  const { t } = useTranslation()
  const translateMetric = useTranslateMetric()
  const tokens = tokenizeFormula(formula)

  if (tokens.length === 0) {
    return (
      <div className="FormulaDisplayCont">
        <span className="placeholder">
          ex) (Total Distance + 2 x HSR Distance) x Acceleration Count
        </span>
      </div>
    )
  }

  const getMetricName = (key: string): string => {
    const metric = availableMetrics.find((m) => m.key === key)
    if (!metric) return key
    return translateMetric(metric)
  }

  const renderToken = (token: ReturnType<typeof tokenizeFormula>[0], index: number) => {
    switch (token.type) {
      case "operator":
        return (
          <span key={index} className="token operator">
            {getOperatorDisplay(token.value)}
          </span>
        )

      case "paren":
        return (
          <span key={index} className="token paren">
            {token.value}
          </span>
        )

      case "number":
        return (
          <span key={index} className="token number">
            {token.value}
          </span>
        )

      case "metric":
        // 테스트 모드에서는 실제 값 표시
        if (showResult && testValues && testValues[token.value] !== undefined) {
          return (
            <Chip
              key={index}
              type="default-small"
              label={testValues[token.value].toLocaleString()}
            />
          )
        }
        return <Chip key={index} type="default-small" label={getMetricName(token.value)} />

      default:
        return null
    }
  }

  return (
    <div className="FormulaDisplayCont">
      {tokens.map(renderToken)}
      {showResult && result !== null && (
        <>
          <span className="token operator">=</span>
          <span className="token number">{result?.toLocaleString()}</span>
        </>
      )}
    </div>
  )
}
